import {
    type DeepPartial,
    MoneyMapAccountStatus,
    MoneyMapAccountType,
    type MoneyMapAccountWithAccountDetails,
    NEW_APEX_IM_ACCOUNT_OPTION,
} from '@fruitful/types';

import { BACKDOOR_ROTH_ENABLED_IN_GROVE } from '../../config/FruitfulEnvironment';
import type { MembershipDetails } from '../../entities/entityTypes';
import {
    type BackdoorRothRuleSplit,
    isAccountBackdoorRoth,
    isApexAccountRothRegistration,
    splitBackdoorRothRuleAccounts,
} from './goalFormBackdoorRothUtils';

export interface ResolvedBackdoorRothFormAccounts {
    backdoorSplitFromRule: BackdoorRothRuleSplit;
    presetSplit: BackdoorRothRuleSplit;
    /** Shown in primary "Account settings" (Roth when editing an existing backdoor pair). */
    primaryAccountForFormDefaults:
        | DeepPartial<MoneyMapAccountWithAccountDetails>
        | undefined;
    /** Trad side preset when a goal/other preset encodes a backdoor pair; else `moneyMapAccountForTransfer`. */
    transferAccountPreset:
        | DeepPartial<MoneyMapAccountWithAccountDetails>
        | undefined;
}

/**
 * Resolves which MMA rows to hydrate for primary vs transfer panels for goal and other-account rules.
 */
export const resolveBackdoorRothFormAccounts = (
    ruleMoneyMapAccount:
        | DeepPartial<MoneyMapAccountWithAccountDetails>
        | undefined,
    accountPreset: DeepPartial<MoneyMapAccountWithAccountDetails> | undefined,
    accounts: MoneyMapAccountWithAccountDetails[],
): ResolvedBackdoorRothFormAccounts => {
    const backdoorSplitFromRule = splitBackdoorRothRuleAccounts(
        ruleMoneyMapAccount,
        accounts,
    );
    const presetSplit = splitBackdoorRothRuleAccounts(accountPreset, accounts);

    const primaryAccountForFormDefaults = backdoorSplitFromRule.isBackdoorRoth
        ? backdoorSplitFromRule.rothAccount
        : ruleMoneyMapAccount;

    const transferAccountPreset = presetSplit.isBackdoorRoth
        ? presetSplit.tradAccount
        : accountPreset?.moneyMapAccountForTransfer;

    return {
        backdoorSplitFromRule,
        presetSplit,
        primaryAccountForFormDefaults,
        transferAccountPreset,
    };
};

/**
 * Resolves the selected transfer Trad account for default form values (matches prior GoalForm logic).
 */
export const resolveExistingMoneyMapTransferAccountForDefaults = (options: {
    backdoorSplitFromRule: BackdoorRothRuleSplit;
    ruleMoneyMapAccount:
        | DeepPartial<MoneyMapAccountWithAccountDetails>
        | undefined;
    transferAccountPreset:
        | DeepPartial<MoneyMapAccountWithAccountDetails>
        | undefined;
    accounts: MoneyMapAccountWithAccountDetails[];
}): DeepPartial<MoneyMapAccountWithAccountDetails> | undefined => {
    const {
        backdoorSplitFromRule,
        ruleMoneyMapAccount,
        transferAccountPreset,
        accounts,
    } = options;

    if (backdoorSplitFromRule.isBackdoorRoth) {
        const trad = backdoorSplitFromRule.tradAccount;
        if (trad?.id != null && trad.id !== NEW_APEX_IM_ACCOUNT_OPTION.value) {
            return (
                accounts.find((a) => a.id === trad.id) ??
                (trad as MoneyMapAccountWithAccountDetails)
            );
        }

        if (
            trad?.id === NEW_APEX_IM_ACCOUNT_OPTION.value &&
            trad?.accountDetails?.name != null
        ) {
            return accounts.find(
                (a) =>
                    a.type === MoneyMapAccountType.APEX_INVESTMENT_MANAGEMENT &&
                    a.accountDetails.name.toLowerCase() ===
                        trad.accountDetails?.name?.toLowerCase(),
            );
        }

        return trad as MoneyMapAccountWithAccountDetails | undefined;
    }

    if (ruleMoneyMapAccount?.moneyMapAccountIdForTransfer != null) {
        return accounts.find(
            (a) => a.id === ruleMoneyMapAccount.moneyMapAccountIdForTransfer,
        );
    }

    if (
        transferAccountPreset?.id == null &&
        transferAccountPreset?.accountDetails?.name != null
    ) {
        return accounts.find(
            (a) =>
                a.type === transferAccountPreset?.type &&
                a.accountDetails.name.toLowerCase() ===
                    transferAccountPreset?.accountDetails?.name?.toLowerCase(),
        );
    }

    return ruleMoneyMapAccount?.moneyMapAccountForTransfer;
};

/**
 * Default display names and primary owner for new Apex Trad/Roth rows when using the Backdoor Roth goal preset.
 *
 * Roth uses possessive first name + "Backdoor Roth IRA" (e.g. `Bob's Backdoor Roth IRA` when the member's first name is Bob).
 *
 * `userIndex` selects which member the preset is for — `0` for the primary member and `1` for the partner on a joint membership.
 */
export const getBackdoorRothPersonalizedAccountDefaults = (
    membership: MembershipDetails | null | undefined,
    userIndex = 0,
):
    | {
          rothAccountName: string;
          tradAccountName: string;
          primaryUserId: string;
      }
    | undefined => {
    const user = membership?.users?.[userIndex];
    if (user == null) {
        return undefined;
    }
    const firstName = (user.firstName ?? '').trim() || 'Member';
    return {
        rothAccountName: `${firstName}'s Backdoor Roth IRA`,
        tradAccountName: `${firstName}'s Trad IRA`,
        primaryUserId: user.id,
    };
};

/**
 * Label for a Backdoor Roth preset button/tile, personalized with the member's first name
 * (e.g. `Bob's Backdoor Roth`). Falls back to `Backdoor Roth` when no first name is available.
 */
export const getBackdoorRothPresetLabel = (
    membership: MembershipDetails | null | undefined,
    userIndex = 0,
): string => {
    const firstName = (membership?.users?.[userIndex]?.firstName ?? '').trim();
    return firstName ? `${firstName}'s Backdoor Roth` : 'Backdoor Roth';
};

export interface BackdoorRothPanelVisibility {
    shouldShowBackdoorCheckbox: boolean;
    shouldShowBackdoorTradPanel: boolean;
    shouldDisableBackdoorCheckbox: boolean;
    /** True when an existing (EXISTS) Roth account already paired with a Trad IRA is selected — checkbox + Trad panel must be locked on. */
    shouldForceBackdoorRothChecked: boolean;
    /** True when any account (proposed or existing) is part of a known backdoor pair — used to auto-populate the Trad side. */
    isKnownBackdoorRoth: boolean;
}

/**
 * Shared visibility for the backdoor checkbox and Trad IRA panel (goal + other-account rules).
 * Gated by `BACKDOOR_ROTH_ENABLED_IN_GROVE` (see BACKDOOR_ROTH_GROVE_PRODUCTION_GATE in FruitfulEnvironment).
 */
export const getBackdoorRothPanelVisibility = (options: {
    isBackdoorRothWatch: boolean | undefined;
    selectedMoneyMapAccountId: string | undefined;
    selectedMoneyMapAccount: MoneyMapAccountWithAccountDetails | undefined;
    apexRothTypeWatch: string | undefined;
    accounts?: MoneyMapAccountWithAccountDetails[];
}): BackdoorRothPanelVisibility => {
    const {
        isBackdoorRothWatch,
        selectedMoneyMapAccountId,
        selectedMoneyMapAccount,
        apexRothTypeWatch,
        accounts,
    } = options;

    if (!BACKDOOR_ROTH_ENABLED_IN_GROVE) {
        return {
            shouldShowBackdoorCheckbox: false,
            shouldShowBackdoorTradPanel: false,
            shouldDisableBackdoorCheckbox: false,
            shouldForceBackdoorRothChecked: false,
            isKnownBackdoorRoth: false,
        };
    }

    const shouldShowBackdoorTradPanel = isBackdoorRothWatch === true;

    const isNewAccount =
        selectedMoneyMapAccountId === NEW_APEX_IM_ACCOUNT_OPTION.value;

    const shouldShowBackdoorCheckbox =
        (isNewAccount ||
            selectedMoneyMapAccount?.type ===
                MoneyMapAccountType.APEX_INVESTMENT_MANAGEMENT) &&
        isApexAccountRothRegistration(
            selectedMoneyMapAccount,
            apexRothTypeWatch,
        );

    const isExistsAccount =
        selectedMoneyMapAccount?.status === MoneyMapAccountStatus.EXISTS;

    const isKnownBackdoorRoth =
        !isNewAccount &&
        selectedMoneyMapAccount != null &&
        isAccountBackdoorRoth(selectedMoneyMapAccount, accounts ?? []);

    const shouldDisableBackdoorCheckbox =
        shouldShowBackdoorCheckbox && isExistsAccount;

    return {
        shouldShowBackdoorCheckbox,
        shouldShowBackdoorTradPanel,
        shouldDisableBackdoorCheckbox,
        shouldForceBackdoorRothChecked: isKnownBackdoorRoth && isExistsAccount,
        isKnownBackdoorRoth,
    };
};
